/**
 * Date formatting utilities for consistent timezone handling across the application.
 * All dates are displayed in America/Chicago timezone.
 */

const CHICAGO_TIMEZONE = 'America/Chicago';

/**
 * Format a date string or Date object to Chicago timezone date string
 * @param date - Date string or Date object
 * @returns Formatted date string (e.g., "1/12/2026")
 */
export function formatChicagoDate(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  try {
    // Handle date-only strings (YYYY-MM-DD) without timezone conversion
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      return `${month}/${day}/${year}`;
    }
    
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      timeZone: CHICAGO_TIMEZONE,
      month: 'numeric',
      day: 'numeric',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

/**
 * Format a date string or Date object to Chicago timezone date and time string
 * @param date - Date string or Date object
 * @returns Formatted date and time string (e.g., "1/12/2026, 11:53:45 AM")
 */
export function formatChicagoDateTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      timeZone: CHICAGO_TIMEZONE,
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '';
  }
}

/**
 * Format a date string or Date object to Chicago timezone time string only
 * @param date - Date string or Date object
 * @returns Formatted time string (e.g., "11:53:45 AM")
 */
export function formatChicagoTime(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString('en-US', {
      timeZone: CHICAGO_TIMEZONE,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

/**
 * Format a date string or Date object to Chicago timezone short date and time
 * @param date - Date string or Date object
 * @returns Formatted date and time string without seconds (e.g., "1/12/2026, 11:53 AM")
 */
export function formatChicagoDateTimeShort(date: string | Date | null | undefined): string {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('en-US', {
      timeZone: CHICAGO_TIMEZONE,
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '';
  }
}
